import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquare, Send, Loader2, Bot, Headset } from "lucide-react";

export default function ChatTab({ api }) {
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/chat/sessions");
      setSessions(data.sessions);
    } catch { /* silent */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openThread = useCallback(async (sessionId) => {
    try {
      const { data } = await api.get(`/admin/chat/sessions/${sessionId}`);
      setActive(data.session);
      setMessages(data.messages);
    } catch { toast.error("Could not load conversation."); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadSessions();
    const t = setInterval(loadSessions, 6000);
    return () => clearInterval(t);
  }, [loadSessions]);

  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => openThread(active.session_id), 4000);
    return () => clearInterval(t);
  }, [active?.session_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim() || !active || sending) return;
    setSending(true);
    try {
      await api.post(`/admin/chat/sessions/${active.session_id}/reply`, { text: reply.trim() });
      setReply("");
      toast.success("Reply sent — the visitor now sees you as Wynova Support.");
      openThread(active.session_id);
      loadSessions();
    } catch { toast.error("Reply failed."); }
    setSending(false);
  };

  const resumeAi = async () => {
    try {
      await api.post(`/admin/chat/sessions/${active.session_id}/resume-ai`, {});
      toast.success("AI assistant resumed for this conversation.");
      openThread(active.session_id);
    } catch { toast.error("Could not resume AI."); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="admin-chat">
      <div className="lg:col-span-1 space-y-3">
        {sessions.length === 0 && (
          <div className="rounded-3xl border border-dashed border-slate-300 dark:border-slate-700 p-12 text-center" data-testid="admin-chat-empty">
            <MessageSquare className="w-8 h-8 mx-auto text-slate-300 dark:text-slate-600" />
            <p className="mt-3 text-sm text-slate-500">No chat conversations yet. They appear here the moment a visitor messages the website chatbot.</p>
          </div>
        )}
        {sessions.map((s) => (
          <button key={s.session_id} onClick={() => openThread(s.session_id)} data-testid={`admin-chat-session-${s.session_id}`}
                  className={`w-full text-left rounded-2xl border p-4 transition-colors ${
                    active?.session_id === s.session_id
                      ? "border-primary bg-primary/5"
                      : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-primary/40"
                  }`}>
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold truncate">{s.name || `Visitor ${s.session_id.slice(0, 6)}`}</p>
              {s.unread_admin > 0 && (
                <span className="shrink-0 px-2 py-0.5 rounded-full bg-primary text-white text-[10px] font-bold">{s.unread_admin} new</span>
              )}
            </div>
            <p className="mt-1 text-xs text-slate-500 truncate">{s.last_message || "No messages yet"}</p>
            <p className="mt-1 text-[10px] text-slate-400 flex items-center gap-1">
              {s.mode === "human" ? <Headset className="w-3 h-3" /> : <Bot className="w-3 h-3" />}
              {s.mode === "human" ? "You are replying" : "AI answering"} · {new Date(s.updated_at).toLocaleString()}
            </p>
          </button>
        ))}
      </div>
      <div className="lg:col-span-2">
        {!active ? (
          <div className="rounded-3xl border border-dashed border-slate-300 dark:border-slate-700 p-16 text-center text-sm text-slate-500">
            Select a conversation to read it and reply.
          </div>
        ) : (
          <div className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-800">
              <p className="font-semibold text-sm">{active.name || `Visitor ${active.session_id.slice(0, 6)}`}</p>
              {active.mode === "human" ? (
                <button onClick={resumeAi} data-testid="admin-chat-resume-ai"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-500/10 text-xs font-bold hover:bg-slate-500/20 transition-colors">
                  <Bot className="w-3.5 h-3.5" /> Resume AI
                </button>
              ) : (
                <span className="text-xs text-slate-400 inline-flex items-center gap-1.5"><Bot className="w-3.5 h-3.5" /> AI is answering — your reply takes over</span>
              )}
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-3 max-h-[28rem] min-h-64" data-testid="admin-chat-messages">
              {messages.length === 0 && <p className="text-sm text-slate-400 text-center py-10">No messages yet.</p>}
              {messages.map((m) => (
                <div key={m.message_id} className={`flex ${m.role === "agent" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "visitor"
                      ? "bg-slate-100 dark:bg-slate-800 rounded-bl-md"
                      : m.role === "agent"
                        ? "bg-primary text-white rounded-br-md"
                        : "bg-cyan-500/10 text-slate-800 dark:text-slate-100 rounded-bl-md border border-cyan-500/20"
                  }`}>
                    {m.role === "ai" && <p className="text-[10px] font-bold uppercase tracking-wider text-cyan-600 dark:text-cyan-400 mb-1">Nova (AI)</p>}
                    {m.role === "agent" && <p className="text-[10px] font-bold uppercase tracking-wider text-white/70 mb-1">You</p>}
                    {m.text}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
            <form onSubmit={sendReply} className="flex items-center gap-2 px-4 py-3 border-t border-slate-200 dark:border-slate-800">
              <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply as Wynova Support…"
                     data-testid="admin-chat-reply-input" maxLength={2000}
                     className="flex-1 px-4 py-2.5 rounded-full bg-slate-100 dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary/60" />
              <button type="submit" disabled={sending || !reply.trim()} data-testid="admin-chat-reply-send" aria-label="Send reply"
                      className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center hover:bg-blue-700 disabled:opacity-50 transition-colors">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
